/** @jsx jsx */
import { css, jsx } from "@emotion/react"
import { useTheme } from "components/common/Context/ThemeContext"
import PropTypes from "prop-types"
import { useMemo } from "react"

const CMSButton = ({
  type = "primary",
  outline = false,
  children,
  disabled = false,
  onClick,
  className = "",
  dataTestId = "",
  dataTut,
  size = "md",
  title = "",
  href
}) => {
  const theme = useTheme()

  const buttonColors = useMemo(() => {
    if (type === "warning")
      return {
        baseColor: theme.warningBtnNormalBg,
        hoverColor: theme.warningBtnHoverBg,
        activeColor: theme.warningBtnActiveBg
      }
    return {
      baseColor: theme.serviceRequestButton,
      hoverColor: theme.btnBgPrimaryColor,
      activeColor: theme.btnBgPrimaryActiveColor
    }
  }, [theme, type])

  const useStyles = (theme) => {
    return {
      customBtn: css`
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.875rem;
        font-family: ${theme.fontFamilyMedium};
        height: 2.1875rem;
        min-width: ${size === "sm"
          ? "2.1875rem"
          : size === "lg"
          ? "8rem"
          : "5.675rem"};
        max-width: 16rem;
        border-radius: 4px;
        white-space: nowrap;
        padding: 5px 10px;
        cursor: ${disabled ? "not-allowed" : "pointer"};
        opacity: ${disabled ? 0.4 : 1};
        color: ${type === "secondary"
          ? theme.btnSecondaryColor
          : outline
          ? buttonColors.baseColor
          : theme.cancelBtnBg} !important;
        background-color: ${type === "secondary" || outline
          ? theme.cancelBtnBg
          : buttonColors.baseColor};
        box-shadow: ${`0px 1px 4px ${theme.boxShadowColor}`};
        border: ${type === "secondary"
          ? `1px solid ${theme.cancelBtnBg}`
          : outline
          ? `1px solid ${buttonColors.baseColor}`
          : "none"};
        &:focus,
        &:hover {
          background-color: ${type === "secondary"
            ? theme.btnBgSecondaryColor
            : outline
            ? theme.btnBgPrimaryOutlineColor
            : buttonColors.hoverColor};
          border: ${type === "secondary"
            ? `1px solid ${theme.btnBgSecondaryColor}`
            : outline
            ? `1px solid ${buttonColors.baseColor}`
            : "none"};
          outline: none;
          text-decoration: none;
        }
        &:active {
          background-color: ${type === "secondary"
            ? theme.cancelBtnBg
            : outline
            ? theme.tableBorderColor
            : buttonColors.activeColor};
          border: ${type === "secondary"
            ? `1px solid ${theme.btnSecondaryBorderColor}`
            : outline
            ? `1px solid ${buttonColors.baseColor}`
            : "none"};
        }
      `
    }
  }

  const classes = useStyles(theme)

  if (disabled || !href)
    return (
      <button
        type="button"
        className={className}
        onClick={onClick}
        css={[classes.customBtn]}
        data-testid={dataTestId}
        data-tut={dataTut}
        title={title}
        disabled={disabled}
      >
        {children}
      </button>
    )
  return (
    <a
      className={className}
      onClick={onClick}
      css={[classes.customBtn]}
      data-testid={dataTestId}
      data-tut={dataTut}
      title={title}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  )
}
export default CMSButton

CMSButton.propTypes = {
  type: PropTypes.string,
  outline: PropTypes.bool,
  children: PropTypes.node,
  onClick: PropTypes.func,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  dataTestId: PropTypes.string,
  dataTut: PropTypes.string,
  size: PropTypes.string,
  title: PropTypes.string,
  href: PropTypes.string
}
